import { OnlineCalculatorsApiService } from '@esprit/online-calculators';
import { configureStore as reduxToolkitConfigureStore } from '@reduxjs/toolkit';
import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import createSagaMiddelware from 'redux-saga';
import i18n from '../../i18n';
import {
    AppointmentsApiService,
    ChatApiService,
    ContactFormSubmissionService,
    CustomerBaseApiService,
    CustomerMasterDataApiService,
    EmployeeMasterDataApiService,
    FinnovaApiService,
    I18ImportService,
    IamApiService,
    LoggingApiService,
    NotificationsApiService,
    PortalAccountApiService,
    ProcessEngineApiService,
    SecureDocumentStoreApiService,
    SecureInboxApiService,
    SecurityApiService,
    SettingsApiService,
    SynchronizationApiService,
} from '../api/services';
import { ProductPermission } from '../enum/product-permission';
import { TenantCodes } from '../enum/tenant';
import type { IRootState } from './root-reducer';
import { createRootReducer } from './root-reducer';
import rootSaga from './root-saga';

export const history = createBrowserHistory({
    basename: process.env.NODE_ENV === 'test' ? undefined : `${process.env.PUBLIC_URL || ''}/${i18n.language}`,
});

let mocks = {} as any;
if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_MOCK === 'true') {
    mocks = require('../api/services/mock');
}

const mockTenant = TenantCodes.BSU;
const mockAuthLevel = ProductPermission.STRONG;

export function configureStore(initialState: Partial<IRootState>, context: object = {}) {
    const sagaMiddleWare = createSagaMiddelware({
        context: process.env.REACT_APP_MOCK
            ? /* eslint-disable @typescript-eslint/no-unsafe-call */
              {
                  appointmentsApiService: new mocks.MockAppointmentsApiService(),
                  chatApiService: new mocks.MockChatApiService(),
                  contactFormService: new mocks.MockContactFormSubmissionService(),
                  customerBaseApiService: new mocks.MockCustomerBaseApiService(mockAuthLevel),
                  customerMasterDataApiService: new mocks.MockCustomerMasterDataApiService(),
                  employeeMasterDataApiService: new mocks.MockEmployeeMasterDataApiService(),
                  finnovaApiService: new mocks.MockFinnovaApiService(mockAuthLevel),
                  i18ImportService: new I18ImportService(),
                  iamApiService: new mocks.MockIamApiService(),
                  loggingApiService: new mocks.MockLoggingApiService(),
                  notificationsApiService: new mocks.MockNotificationsApiService(),
                  onlineCalculatorsApiService: new mocks.MockOnlineCalculatorApiService(),
                  portalAccountApiService: new mocks.MockPortalAccountApiService(),
                  processEngineApiService: new mocks.MockProcessEngineApiService(),
                  secureDocumentStoreApiService: new mocks.MockSecureDocumentStoreApiService(),
                  secureInboxApiService: new mocks.MockSecureInboxApiService(),
                  settingsApiService: new mocks.MockSettingsApiService(mockTenant, mockAuthLevel),
                  synchronizationApiService: new mocks.MockSynchronizationApiService(),
                  userInfoApiService: new mocks.MockSecurityApiService(mockAuthLevel),
                  ...context,
              } /* eslint-enable @typescript-eslint/no-unsafe-call */
            : {
                  appointmentsApiService: new AppointmentsApiService(),
                  chatApiService: new ChatApiService(),
                  contactFormService: new ContactFormSubmissionService(),
                  customerBaseApiService: new CustomerBaseApiService(),
                  customerMasterDataApiService: new CustomerMasterDataApiService(),
                  employeeMasterDataApiService: new EmployeeMasterDataApiService(),
                  finnovaApiService: new FinnovaApiService(),
                  i18ImportService: new I18ImportService(),
                  iamApiService: new IamApiService(),
                  loggingApiService: new LoggingApiService(),
                  notificationsApiService: new NotificationsApiService(),
                  onlineCalculatorsApiService: new OnlineCalculatorsApiService(),
                  portalAccountApiService: new PortalAccountApiService(),
                  processEngineApiService: new ProcessEngineApiService(),
                  secureDocumentStoreApiService: new SecureDocumentStoreApiService(),
                  secureInboxApiService: new SecureInboxApiService(),
                  settingsApiService: new SettingsApiService(),
                  synchronizationApiService: new SynchronizationApiService(),
                  userInfoApiService: new SecurityApiService(),
                  ...context,
              },
    });

    const store = reduxToolkitConfigureStore({
        reducer: createRootReducer(history),
        middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat([routerMiddleware(history), sagaMiddleWare]),
        devTools: process.env.NODE_ENV === 'development',
        preloadedState: initialState,
    });

    sagaMiddleWare.run(rootSaga);

    return store;
}
